import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { EntityState, serializeAxiosError, IQueryParams } from 'app/shared/reducers/reducer.utils';
import { IKpiTimeResponse, IKpiResponse } from 'app/shared/model/indicator-dto';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { DataType } from 'app/shared/model/enumerations/dataType';
import { IIndicatorDto, defaultValue } from 'app/shared/model/indicator-dto.model';

const initialState: EntityState<IIndicatorDto> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

export interface ISpeedProps extends IQueryParams {
  sensor: string | null;
  periodicType: PeriodicType | null;
  truckImplementId?: number | string;
  companyId?: number | null;
  dataType: DataType | null;
  dateStart?: string | null;
  dateEnd?: string | null;
}

const apiUrl = '/api/indicator-average';

// Actions

export const getSpeedPerTime = createAsyncThunk(
  'indicatorSpeed/fetch_speed_per_time_periodic',
  async ({ sensor, periodicType, truckImplementId, companyId, dataType, dateStart, dateEnd }: ISpeedProps) => {
    const apiUrlComplete = `${apiUrl}/time/${sensor}${truckImplementId ? `/${truckImplementId}` : ''}/${periodicType}${
      companyId ? `?searchCompanyId=${companyId}` : ''
    }`;
    const params = {
      dataType,
      dateStart,
      dateEnd,
    };
    const response = await axios.get<IKpiTimeResponse>(apiUrlComplete, { params });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getSpeedPerVehicleAndPlate = createAsyncThunk(
  'indicatorSpeed/fetch_speed_per_vehicle_and_plate_periodic',
  async ({ sensor, periodicType, companyId, dataType, dateStart, dateEnd }: ISpeedProps) => {
    const apiUrlComplete = `${apiUrl}/statistics/${sensor}/${periodicType}${companyId ? `?searchCompanyId=${companyId}` : ''}`;
    const params = {
      dataType,
      dateStart,
      dateEnd,
    };
    const response = await axios.get<IKpiResponse>(apiUrlComplete, { params });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

// Slice

const indicatorSpeedSlice = createSlice({
  name: 'indicatorSpeed',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getSpeedPerTime.fulfilled, (state, action) => {
        state.loading = false;
        state.entity.kpiPerTime = action.payload;
      })
      .addCase(getSpeedPerTime.pending, state => {
        state.loading = true;
      })
      .addCase(getSpeedPerTime.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      })
      .addCase(getSpeedPerVehicleAndPlate.fulfilled, (state, action) => {
        state.loading = false;
        state.entity.kpiPerVehicleAndStatistics = action.payload;
      })
      .addCase(getSpeedPerVehicleAndPlate.pending, state => {
        state.loading = true;
      })
      .addCase(getSpeedPerVehicleAndPlate.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      });
  },
});

export const { reset } = indicatorSpeedSlice.actions;

export default indicatorSpeedSlice.reducer;
